import React, { useEffect, useState } from "react";
import { useRouter } from "next/router";
import Landing from "@components/Landing";
import { useAuth } from "@context";
import RPC from "@components/SolanaWallet/solanaRPC";
import Spinner from '@util/spinner';
import { safeLocalStorage } from '@utils/localstorage';
import { extractSubdomain } from '@utils/subdomain';

const Main = () => {
  const router = useRouter();
  const { provider, setWalletAddress, setAccountInfo, loggedIn, loginFlag, authInitialized } = useAuth();
  const token = safeLocalStorage.getItem('token');
  // Gate visible UI until we've finished the client-side auth check.
  // Without this, SSR renders <Landing /> (no localStorage on the server),
  // the browser paints the login page, and only THEN does useEffect read
  // the token and redirect — producing a visible login-page "blink" for
  // returning users.
  const [authChecked, setAuthChecked] = useState(false);

  // Returning users: skip login spinner and go straight to the app (setUserDetail also redirects).
  // Route by subdomain to mirror legacy post-login behavior — 'my' → owner dashboard,
  // 'drive' → test-drive flow depending on has_test_drive.
  useEffect(() => {
    if (!token?.user) {
      setAuthChecked(true);
      return;
    }

    // Returning user landing directly on the activate login step: skip it and
    // continue the activate flow at step 2, mirroring redirectAuthenticatedUser's
    // activate-login/activate handling during a fresh login. Without this the
    // render guard below keeps the spinner up forever (token?.user is truthy but
    // no redirect fires for a non-'/' path).
    if (router.pathname === '/activate-login' || router.pathname === '/activate') {
      router.replace('/activate-account');
      return;
    }

    if (router.pathname !== '/') {
      setAuthChecked(true);
      return;
    }

    const subdomain = extractSubdomain();
    const target = subdomain === 'my'
      ? '/owner/dashboard'
      : (token.user.has_test_drive ? '/dashboard' : '/testdrive');

    router.replace(target);
    // Intentionally do NOT setAuthChecked(true) — we're navigating away,
    // keep the spinner visible until the new page takes over.
  }, [token, router]);

  useEffect(() => {
    if (token?.user) return;
    if (loginFlag && provider) {
      getAccounts();
    }
  }, [loginFlag, provider, token]);

  const getAccounts = async () => {
    if (!provider) return;
    try {
      const rpc = new RPC(provider);
      const walletAddress = await rpc.getAccounts();
      if (!Array.isArray(walletAddress) || walletAddress.length === 0) return;
      const addr = walletAddress[0];
      if (typeof addr !== 'string' || !addr) return;
      setWalletAddress(addr);
      await setAccountInfo(addr);
    } catch (e) {
      console.error('getAccounts:', e);
    }
  };

  // Show spinner during any "redirect imminent or in progress" state:
  //   - !authChecked:       initial mount, localStorage not yet read
  //   - !authInitialized:   web3auth.init() still running (covers the post-
  //                         Discord-OAuth redirect-back, where init runs async
  //                         and loginFlag is about to be set)
  //   - token?.user:        returning user, redirect about to fire
  //   - loggedIn || loginFlag: mid-Discord-login in flight
  // Signout: web3AuthLogout resets loggedIn / loginFlag synchronously, so
  // <Landing /> renders correctly after sign-out.
  if (!authChecked || !authInitialized || token?.user || loggedIn || loginFlag) {
    return <Spinner loading />;
  }

  return <Landing />;

};

export default Main;